import store from '@/store'
import router from '@/router'
import VueRouter from 'vue-router'
import Status from 'App/Http/Status'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { attachAbortSignal } from 'App/Http/pendingRequests'

/*
|--------------------------------------------------------------------------
| Axios interceptors
|--------------------------------------------------------------------------
|
| Global Axios interceptors run before a request is sent and can transform
| a response once it is received.
|
*/

/**
 * The API endpoints (w/out base url - api/v1) that should not have
 * an Authorization header & bearer token attached to the request.
 *
 * @type {Array}
 */
const excluded = ['v1/login', 'v1/login/options', 'v1/sso/token']
const loginUrls = ['v1/login', 'v1/sso/token']

/**
 * Axios global request interceptors.
 *
 * @type {Array}
 */
const request = [
	attachAbortSignal,
	/**
	 * Ensure session has not exceeded 15 days, then attach the bearer token.
	 * If session is expired, the API call is not sent and user is redirected to login.
	 *
	 * @param {Object} config
	 * @return {Object|Promise}
	 */
	async config => {
		const url = config.url

		if (excluded.includes(url)) {
			return config
		}

		const sessionValid = await store.dispatch('auth/checkSessionExpiry')
		if (!sessionValid) {
			await unauthenticatedErrorHandler()
			return Promise.reject(new Error('Session expired'))
		}

		const { access_token } = store.state.auth.session
		if (access_token) {
			config.headers.Authorization = `Bearer ${access_token}`
		}

		return config
	},
]

/**
 * Handle an unauthenticated error response  from the API.
 *
 @return {void}
 */
const unauthenticatedErrorHandler = async () => {
	await store.dispatch('auth/logout')

	if (router.currentRoute.name === 'login') {
		return
	}

	const { isNavigationFailure, NavigationFailureType } = VueRouter

	try {
		await router.replace({ name: 'login' })
	} catch (failure) {
		const hasFailed = isNavigationFailure(
			failure,
			NavigationFailureType.createRouterError
		)

		if (hasFailed) {
			console.log('The session has expired.')
		}
	}
}

/**
 * Axios global response interceptors.
 *
 * @type {Array}
 */
const response = [
	{
		/**
		 * Success response interceptor.
		 *
		 * @param {Object} response
		 * @return {Object}
		 */
		success: response => {
			const userRoles = get(response, 'data.data.roles', [])
			const url = get(response, 'config.url', '')

			// Restrict some user role to login into OMD,
			// until we get permission based login support in API
			if (loginUrls.includes(url)) {
				const restrictedRole = [
					'transcriptionist',
					'transcriptionist-manager',
					'transcriptionist-qa',
				]
				const allowedRoles = []
				userRoles.forEach(({ name }) => {
					if (!restrictedRole.includes(name)) {
						allowedRoles.push(name)
					}
				})
				if (!isEmpty(userRoles) && isEmpty(allowedRoles)) {
					return {
						status: 401,
					}
				}
			}
			return response
		},

		/**
		 * Error response interceptor.
		 *
		 * @param {Error} error
		 * @return {Promise}
		 */
		error: async error => {
			const url = error?.config?.url || error?.response?.config?.url || ''

			if (
				error?.response?.status === Status.UNAUTHENTICATED &&
				!excluded.includes(url)
			) {
				await unauthenticatedErrorHandler()
			}

			return Promise.reject(error)
		},
	},
]

export default {
	request,
	response,
}
